import type { PropsWithChildren } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl'
import { DEFAULT_LOCALE, type SupportedLocale } from './util/locale'
import { isSupportedLocale } from './util/localeMatcher'
import { usePseudoloc, type PseudolocMode } from './PseudolocContext'
import { setCurrentIntl } from './util/currentIntl'
type LocaleMessages = Record<string, string>
const emptyMessages: LocaleMessages = {}

type MessageTransformer = (messages: LocaleMessages) => LocaleMessages
type MessageEntry = {
  defaultMessage: string
  description?: string
}

interface I18nProviderProps {
  messages?: LocaleMessages
}

function mapLocaleMessages(messages: Record<string, MessageEntry>): LocaleMessages {
  return Object.fromEntries(
    Object.entries(messages).map(([key, value]) => [key, value.defaultMessage]),
  )
}

export function loadLocaleMessages(locale: SupportedLocale, pseudolocMode: PseudolocMode): Promise<LocaleMessages> {
  const lang = locale.split('-')[0]
  if (lang === 'en' && pseudolocMode === 'off') return Promise.resolve(emptyMessages)
  return import(
    /* webpackChunkName: "lang-[request]" */
    /* webpackMode: "lazy" */
    `./lang/${lang}.json`
  )
    .then((mod) => mapLocaleMessages(mod.default as Record<string, MessageEntry>))
    .catch(() => emptyMessages)
}

export function I18nProvider({
  messages,
  children,
}: PropsWithChildren<I18nProviderProps>) {
  const geoLocale = useAppSelector((state) => state.geo.locale)
  const locale: SupportedLocale = geoLocale && isSupportedLocale(geoLocale) ? geoLocale : DEFAULT_LOCALE
  const { mode } = usePseudoloc()
  const [transformers, setTransformers] = useState<Partial<Record<PseudolocMode, MessageTransformer>>>({})
  const [localeMessages, setLocaleMessages] = useState<LocaleMessages>(messages ?? emptyMessages)

  useEffect(() => {
    if (messages) {
      setLocaleMessages(messages)
      return
    }

    let isMounted = true

    loadLocaleMessages(locale, mode).then((loadedMessages) => {
      if (isMounted) {
        setLocaleMessages(loadedMessages)
      }
    })

    return () => {
      isMounted = false
    }
  }, [locale, messages, mode])

  useEffect(() => {
    if (mode === 'off') return

    // Note: Both transformers are loaded regardless of the active mode since
    // they live in the same module. This avoids re-importing on mode switch.
    import('./util/pseudoloc').then((module) => {
      setTransformers((prev) => ({
        ...prev,
        full: module.pseudolocalizeMessages,
        length: module.lengthPseudolocalizeMessages,
      }))
    })
  }, [mode])

  const processedMessages = useMemo(() => {
    if (mode !== 'off') {
      const transformer = transformers[mode]
      if (transformer) {
        return transformer(localeMessages)
      }
    }
    return localeMessages
  }, [localeMessages, mode, transformers])

  const intl = useMemo(() => {
    return createIntl({
      locale,
      messages: processedMessages,
      defaultLocale: DEFAULT_LOCALE,
      onError(err) {
        if (err.code === 'MISSING_TRANSLATION') {
          return
        }
        console.error(err)
      },
    }, createIntlCache())
  }, [locale, processedMessages])

  setCurrentIntl(intl)

  return (
    <RawIntlProvider value={intl}>
      {children}
    </RawIntlProvider>
  )
}
